import type { JSX } from 'react';

import type { Teilnehmerzeile } from '../api/buchungen';
import { reicheDatei } from '../komponenten/datei';

/**
 * DIE TEILNEHMERLISTE JE TERMIN (BUC-53, BUC-70).
 *
 * "Teilnehmerliste je Termin als DRUCK- UND EXPORTANSICHT MIT
 * ANWESENHEITSSPALTE, unter Beachtung der Rechte nach Kapitel 15."
 *
 * Bis hierher gab es eine Bildschirmtabelle mit vier Spalten. Was fehlte,
 * sind die drei Dinge, die die Anforderung nennt — und alle drei entscheiden
 * sich am Kurstag, nicht am Schreibtisch:
 *
 *   DIE ANWESENHEIT. Wer am Tor steht, hakt ab. Das Erfassen dieser Haken im
 *   System ist BUC-55 und Release 2; die SPALTE verlangt BUC-53 jetzt. Sie
 *   steht deshalb als leeres Kästchen da, nicht als Bedienelement, das
 *   nichts täte.
 *
 *   DER DRUCK. Ein Bildschirm hilft am Tor nicht. Die Druckansicht lässt die
 *   Maske weg und behält die Liste — mit Terminkopf, damit ein abgelegtes
 *   Blatt später noch sagt, wozu es gehört.
 *
 *   DER EXPORT. Als Datei, damit die Liste weiterverarbeitet werden kann.
 *
 * DIE DATEI ENTSTEHT IM BROWSER und nicht auf dem Server: Die Zeilen liegen
 * bereits vor — geholt über einen Endpunkt, der die Rechte nach Kapitel 15
 * prüft. Ein zweiter Weg zu denselben Daten wäre eine zweite Rechteprüfung,
 * die auseinanderlaufen kann.
 */

/** RFC 4180: Anführungszeichen verdoppeln, das Feld einfassen, wenn nötig. */
function alsFeld(wert: string): string {
  return /[";\n\r]/.test(wert) ? `"${wert.replace(/"/g, '""')}"` : wert;
}

/**
 * Die Liste als Semikolon-Datei.
 *
 * SEMIKOLON UND NICHT KOMMA: Excel in deutscher Spracheinstellung liest eine
 * Komma-Datei als eine einzige Spalte. Die Liste geht an Leute, die sie
 * öffnen und nicht einlesen.
 *
 * MIT BOM: Ohne ihn zeigt Excel „Müller" als „MÃ¼ller".
 */
export function alsSemikolondatei(
  zeilen: readonly Teilnehmerzeile[],
  terminname: string,
): string {
  const kopf = ['Termin', 'Buchung', 'Kunde', 'Platz', 'Teilnehmer', 'Anwesend'];
  const koerper = zeilen.map((z) =>
    [
      terminname,
      z.buchungsnummer,
      z.kundenname,
      String(z.position),
      /* BUC-70: Ein fehlender Name ist eine Aussage, kein leeres Feld. */
      z.name ?? 'Name fehlt',
      /* BUC-53: die Spalte zum Abhaken. Erfasst wird sie nach BUC-55 (R2). */
      '',
    ].map(alsFeld).join(';'),
  );
  /* U+FEFF als Fluchtfolge und nicht wörtlich — ESLint verbietet
     unsichtbare Zeichen im Quelltext, und zu Recht: Hier stünde ein
     Zeichen, das man nicht sieht und nicht sucht. */
  return `\uFEFF${[kopf.join(';'), ...koerper].join('\r\n')}\r\n`;
}

export function Teilnehmerliste({
  zeilen,
  terminname,
}: {
  readonly zeilen: readonly Teilnehmerzeile[];
  readonly terminname: string;
}): JSX.Element {
  function exportiere(): void {
    const datei = new Blob([alsSemikolondatei(zeilen, terminname)], {
      type: 'text/csv;charset=utf-8',
    });
    // Auftrag CRM-168: dieselbe Hilfe wie jeder Download; eine CSV bleibt reiner Download.
    reicheDatei(datei, `Teilnehmerliste ${terminname}.csv`.replace(/[/\\:*?"<>|]/g, '-'));
  }

  const ohneNamen = zeilen.filter((z) => z.name === null).length;

  return (
    <div className="karte teilnehmerliste">
      <div className="teilnehmerliste__kopf">
        <h2>Teilnehmerliste des Termins</h2>
        <div className="knopfreihe teilnehmerliste__werkzeuge">
          <button
            type="button"
            className="knopf"
            onClick={() => {
              window.print();
            }}
          >
            Drucken
          </button>
          <button type="button" className="knopf" onClick={exportiere}>
            Als Datei
          </button>
        </div>
      </div>

      {/*
        NUR IM DRUCK: Ein abgelegtes Blatt muss sagen, wozu es gehoert. Am
        Bildschirm steht der Termin schon in der Maske darueber.
      */}
      <p className="teilnehmerliste__druckkopf">{terminname}</p>

      <p className="hilfe">
        Alle Buchungen dieses Termins. Offene Plätze stehen als eigene Zeile — wer am Kurstag
        mit dieser Liste am Tor steht, muss sehen, dass ein Name noch fehlt.
        {ohneNamen > 0 && ` Zurzeit fehlen ${String(ohneNamen)} Namen.`}
      </p>

      <div className="tabellenhuelle tabellenhuelle--telefon">
        <table className="liste__tabelle">
          <caption className="nur-vorlesen">Teilnehmer dieses Termins</caption>
          <thead>
            <tr>
              <th scope="col">Buchung</th>
              <th scope="col">Kunde</th>
              <th scope="col">Platz</th>
              <th scope="col">Teilnehmer</th>
              {/*
                BUC-53 verlangt die Spalte, BUC-55 das Erfassen — und das ist
                Release 2. Sie steht deshalb als Kaestchen zum Abhaken auf
                Papier und nicht als Schalter, der nichts bewirkt.
              */}
              <th scope="col">Anwesend</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={z.id}>
                <td className="liste__nummer">{z.buchungsnummer}</td>
                <td>{z.kundenname}</td>
                <td>{z.position}</td>
                <td>
                  {z.name === null ? <span className="marke marke--warn">Name fehlt</span> : z.name}
                </td>
                <td>
                  <span className="anwesenheitsfeld" aria-label="Anwesenheit, auf dem Ausdruck einzutragen" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="feld__hilfe teilnehmerliste__fuss">
        Die Anwesenheit wird auf dem Ausdruck eingetragen. Ihre Erfassung im System kommt mit
        der Teilnahmebestätigung.
      </p>
    </div>
  );
}
